import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
    ACCOMMODATION_STYLES,
    ACCOMMODATION_PRIORITIES,
    FOOD_PRIORITIES,
    PACE_OPTIONS,
    WAKE_OPTIONS,
    WALKING_OPTIONS,
    INTERESTS,
    RATINGS,
} from '@/lib/preferences';
import { PREFS } from '@/constants/testIds';
import { ArrowLeft, ArrowRight, Check, X, Sparkles, BedDouble, UtensilsCrossed, Compass, Heart } from 'lucide-react';

const STEPS = [
    { id: 1, title: 'Where you sleep', subtitle: 'Stay style, vibe, and nightly budget.', icon: BedDouble },
    { id: 2, title: 'How you eat', subtitle: 'Food vibes, restrictions, daily budget.', icon: UtensilsCrossed },
    { id: 3, title: 'How you travel', subtitle: 'Pace, mornings, and how much you walk.', icon: Compass },
    { id: 4, title: 'What excites you', subtitle: 'Rate each interest — love it, neutral, or skip.', icon: Heart },
];

function VisualSelector({ value, onChange, options, testid }) {
    return (
        <div data-testid={testid} className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {options.map((o) => {
                const Icon = o.icon;
                const active = value === o.value;
                return (
                    <button
                        key={o.value}
                        type="button"
                        onClick={() => onChange(o.value)}
                        data-testid={`${testid}-${o.value}`}
                        className={`text-left rounded-2xl border p-4 transition group ${active
                            ? 'border-[var(--ns-navy)] bg-white shadow-sm'
                            : 'border-[var(--ns-border)] bg-white hover:border-[var(--ns-borderStrong)]'}`}
                    >
                        <div
                            className="w-9 h-9 rounded-xl flex items-center justify-center mb-3"
                            style={{ background: active ? 'var(--ns-navy)' : 'var(--ns-soft)' }}
                        >
                            <Icon size={16} className={active ? 'text-white' : 'text-[var(--ns-clay)]'} />
                        </div>
                        <div className="font-display font-semibold text-[var(--ns-text)]">{o.label}</div>
                        <div className="text-xs text-[var(--ns-text3)] mt-1">{o.hint}</div>
                    </button>
                );
            })}
        </div>
    );
}

function ChipMultiSelect({ values = [], onToggle, options, testid }) {
    return (
        <div data-testid={testid} className="flex flex-wrap gap-2">
            {options.map((o) => {
                const active = values.includes(o.value);
                return (
                    <button
                        key={o.value}
                        type="button"
                        onClick={() => onToggle(o.value)}
                        data-testid={`${testid}-${o.value}`}
                        className={`inline-flex items-center gap-1.5 rounded-full px-4 h-10 text-sm transition border ${active
                            ? 'bg-[var(--ns-navy)] text-white border-[var(--ns-navy)]'
                            : 'bg-white text-[var(--ns-text2)] border-[var(--ns-border)] hover:border-[var(--ns-borderStrong)]'}`}
                    >
                        {active && <Check size={14} />}
                        {o.label}
                    </button>
                );
            })}
        </div>
    );
}

function SegmentedControl({ value, onChange, options, testid }) {
    return (
        <div data-testid={testid} className="grid grid-cols-3 gap-2">
            {options.map((o) => {
                const active = value === o.value;
                return (
                    <button
                        key={o.value}
                        type="button"
                        onClick={() => onChange(o.value)}
                        data-testid={`${testid}-${o.value}`}
                        className={`rounded-2xl px-3 py-3 text-center border transition ${active
                            ? 'border-[var(--ns-navy)] bg-white shadow-sm'
                            : 'border-[var(--ns-border)] bg-white hover:border-[var(--ns-borderStrong)]'}`}
                    >
                        <div className="font-display font-semibold text-sm text-[var(--ns-text)]">{o.label}</div>
                        <div className="text-[11px] text-[var(--ns-text3)] mt-0.5">{o.hint}</div>
                    </button>
                );
            })}
        </div>
    );
}

function BudgetRange({ label, currency = 'SGD', min, max, onMin, onMax, testidMin, testidMax }) {
    const hasError = min !== null && max !== null && min !== '' && max !== '' && Number(min) > Number(max);
    return (
        <div>
            <Label className="text-sm text-[var(--ns-text2)]">{label}</Label>
            <div className="grid grid-cols-2 gap-3 mt-1">
                <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs uppercase tracking-widest text-[var(--ns-text3)]">Min</span>
                    <Input
                        type="number"
                        min={0}
                        value={min ?? ''}
                        onChange={(e) => onMin(e.target.value === '' ? null : Number(e.target.value))}
                        placeholder="0"
                        data-testid={testidMin}
                        className={`h-12 rounded-xl bg-white pl-12 pr-12 text-right ${hasError ? 'border-red-400' : 'border-[var(--ns-border)]'}`}
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[var(--ns-text3)]">{currency}</span>
                </div>
                <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs uppercase tracking-widest text-[var(--ns-text3)]">Max</span>
                    <Input
                        type="number"
                        min={0}
                        value={max ?? ''}
                        onChange={(e) => onMax(e.target.value === '' ? null : Number(e.target.value))}
                        placeholder="0"
                        data-testid={testidMax}
                        className={`h-12 rounded-xl bg-white pl-12 pr-12 text-right ${hasError ? 'border-red-400' : 'border-[var(--ns-border)]'}`}
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[var(--ns-text3)]">{currency}</span>
                </div>
            </div>
            {hasError && (
                <p className="text-xs text-red-500 mt-1.5">Minimum can't be more than maximum.</p>
            )}
        </div>
    );
}

function InterestRater({ interest, value, onChange }) {
    return (
        <div data-testid={PREFS.interestRow} className="flex items-center justify-between gap-4 py-3 border-b border-[var(--ns-border)] last:border-0">
            <div className="font-display font-medium text-[var(--ns-text)]">{interest.label}</div>
            <div className="flex items-center gap-2">
                {RATINGS.map((r) => {
                    const active = value === r.value;
                    return (
                        <button
                            key={r.value}
                            type="button"
                            onClick={() => onChange(r.value)}
                            data-testid={`${PREFS.interestRating}-${interest.key}-${r.value}`}
                            className={`rounded-full px-4 h-9 text-xs font-medium transition border ${active
                                ? 'text-white border-transparent'
                                : 'bg-white text-[var(--ns-text2)] border-[var(--ns-border)] hover:border-[var(--ns-borderStrong)]'}`}
                            style={active ? { background: r.color } : undefined}
                        >
                            {r.label}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

const empty = () => ({
    accommodation: { styles: [], priorities: [], budget_min: null, budget_max: null },
    food: { priorities: [], dietary: '', budget_min: null, budget_max: null },
    travel_style: { pace: null, wake: null, walking: null },
    interests: {},
});

function ensureShape(initial) {
    const e = empty();
    if (!initial) return e;
    return {
        accommodation: { ...e.accommodation, ...(initial.accommodation || {}) },
        food: { ...e.food, ...(initial.food || {}) },
        travel_style: { ...e.travel_style, ...(initial.travel_style || {}) },
        interests: { ...(initial.interests || {}) },
    };
}

export default function PreferenceForm({ initial, onSubmit, submitting, onCancel }) {
    const [step, setStep] = useState(1);
    const [data, setData] = useState(() => ensureShape(initial));

    const update = (path) => (val) => {
        setData((d) => {
            const next = { ...d };
            const keys = path.split('.');
            let ref = next;
            for (let i = 0; i < keys.length - 1; i++) {
                ref[keys[i]] = { ...ref[keys[i]] };
                ref = ref[keys[i]];
            }
            ref[keys[keys.length - 1]] = val;
            return next;
        });
    };

    const togglePriority = (group) => (val) => {
        setData((d) => {
            const arr = new Set(d[group].priorities || []);
            if (arr.has(val)) arr.delete(val); else arr.add(val);
            return { ...d, [group]: { ...d[group], priorities: Array.from(arr) } };
        });
    };

    const setInterest = (key) => (val) => {
        setData((d) => ({ ...d, interests: { ...d.interests, [key]: val } }));
    };

    const StepHeader = STEPS[step - 1];
    const Icon = StepHeader.icon;

    return (
        <div data-testid={PREFS.form} className="ns-card p-6 sm:p-10 mt-6">
            {/* Progress + breadcrumb */}
            <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                <div className="flex items-center gap-3">
                    <span className="w-10 h-10 rounded-2xl flex items-center justify-center" style={{ background: 'var(--ns-soft)' }}>
                        <Icon size={18} className="text-[var(--ns-clay)]" />
                    </span>
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--ns-text3)]">Step {step} of {STEPS.length}</div>
                        <div className="font-display text-xl font-semibold tracking-tight">{StepHeader.title}</div>
                    </div>
                </div>
                <div className="text-xs text-[var(--ns-text3)] hidden sm:block">{StepHeader.subtitle}</div>
            </div>

            <div data-testid={PREFS.progress} className="flex gap-1 mb-8">
                {STEPS.map((s) => (
                    <div
                        key={s.id}
                        className="h-1 flex-1 rounded-full"
                        style={{ background: s.id <= step ? 'var(--ns-clay)' : 'var(--ns-border)' }}
                    />
                ))}
            </div>

            {/* Step 1 */}
            {step === 1 && (
                <div className="space-y-8">
                    <div>
                        <Label className="text-sm text-[var(--ns-text2)] mb-3 block">Stay style</Label>
                        <div data-testid={PREFS.accommodationStyle} className="grid grid-cols-2 sm:grid-cols-3 gap-3">
    {ACCOMMODATION_STYLES.map((o) => {
        const Icon = o.icon;
        const active = (data.accommodation.styles || []).includes(o.value);
        return (
            <button
                key={o.value}
                type="button"
                onClick={() => {
                    const arr = new Set(data.accommodation.styles || []);
                    if (arr.has(o.value)) arr.delete(o.value); else arr.add(o.value);
                    update('accommodation.styles')(Array.from(arr));
                }}
                data-testid={`${PREFS.accommodationStyle}-${o.value}`}
                className={`text-left rounded-2xl border p-4 transition group ${active
                    ? 'border-[var(--ns-navy)] bg-white shadow-sm'
                    : 'border-[var(--ns-border)] bg-white hover:border-[var(--ns-borderStrong)]'}`}
            >
                <div
                    className="w-9 h-9 rounded-xl flex items-center justify-center mb-3"
                    style={{ background: active ? 'var(--ns-navy)' : 'var(--ns-soft)' }}
                >
                    <Icon size={16} className={active ? 'text-white' : 'text-[var(--ns-clay)]'} />
                </div>
                <div className="font-display font-semibold text-[var(--ns-text)]">{o.label}</div>
                <div className="text-xs text-[var(--ns-text3)] mt-1">{o.hint}</div>
                {active && <Check size={14} className="absolute top-3 right-3 text-[var(--ns-navy)]" />}
            </button>
        );
    })}
</div>
                    </div>
                    <div>
                        <Label className="text-sm text-[var(--ns-text2)] mb-3 block">Must-haves (pick any)</Label>
                        <ChipMultiSelect
                            values={data.accommodation.priorities}
                            onToggle={togglePriority('accommodation')}
                            options={ACCOMMODATION_PRIORITIES}
                            testid={PREFS.accommodationPriorities}
                        />
                    </div>
                    <BudgetRange
                        label="Nightly accommodation budget"
                        min={data.accommodation.budget_min}
                        max={data.accommodation.budget_max}
                        onMin={update('accommodation.budget_min')}
                        onMax={update('accommodation.budget_max')}
                        testidMin={PREFS.accommodationBudgetMin}
                        testidMax={PREFS.accommodationBudgetMax}
                    />
                </div>
            )}

            {/* Step 2 */}
            {step === 2 && (
                <div className="space-y-8">
                    <div>
                        <Label className="text-sm text-[var(--ns-text2)] mb-3 block">Food vibes (pick any)</Label>
                        <ChipMultiSelect
                            values={data.food.priorities}
                            onToggle={togglePriority('food')}
                            options={FOOD_PRIORITIES}
                            testid={PREFS.foodPriorities}
                        />
                    </div>
                    <div>
                        <Label className="text-sm text-[var(--ns-text2)] mb-3 block">Dietary requirements</Label>
                        <Textarea
                            value={data.food.dietary || ''}
                            onChange={(e) => update('food.dietary')(e.target.value)}
                            placeholder="e.g., vegetarian, halal, nut allergy"
                            data-testid={PREFS.foodDietary}
                            className="rounded-xl border-[var(--ns-border)] bg-white min-h-[88px]"
                        />
                    </div>
                    <BudgetRange
                        label="Daily food budget"
                        min={data.food.budget_min}
                        max={data.food.budget_max}
                        onMin={update('food.budget_min')}
                        onMax={update('food.budget_max')}
                        testidMin={PREFS.foodBudgetMin}
                        testidMax={PREFS.foodBudgetMax}
                    />
                </div>
            )}

            {/* Step 3 */}
            {step === 3 && (
                <div className="space-y-8">
                    <div>
                        <Label className="text-sm text-[var(--ns-text2)] mb-3 block">Pace</Label>
                        <SegmentedControl value={data.travel_style.pace} onChange={update('travel_style.pace')} options={PACE_OPTIONS} testid={PREFS.pace} />
                    </div>
                    <div>
                        <Label className="text-sm text-[var(--ns-text2)] mb-3 block">Wake-up time</Label>
                        <SegmentedControl value={data.travel_style.wake} onChange={update('travel_style.wake')} options={WAKE_OPTIONS} testid={PREFS.wake} />
                    </div>
                    <div>
                        <Label className="text-sm text-[var(--ns-text2)] mb-3 block">Walking tolerance</Label>
                        <SegmentedControl value={data.travel_style.walking} onChange={update('travel_style.walking')} options={WALKING_OPTIONS} testid={PREFS.walking} />
                    </div>
                </div>
            )}

            {/* Step 4 */}
            {step === 4 && (
                <div>
                    <p className="text-sm text-[var(--ns-text2)] mb-3">Rate each — your answers help the group plan around what everyone actually loves.</p>
                    <div className="rounded-2xl border border-[var(--ns-border)] bg-white px-4 sm:px-6">
                        {INTERESTS.map((i) => (
                            <InterestRater
                                key={i.key}
                                interest={i}
                                value={data.interests[i.key]}
                                onChange={setInterest(i.key)}
                            />
                        ))}
                    </div>
                </div>
            )}

            {/* Nav */}
            <div className="flex items-center justify-between gap-3 mt-10">
                {step === 1 ? (
                    <Button
                        variant="ghost"
                        onClick={onCancel}
                        className="rounded-full h-11 px-5 text-[var(--ns-text2)]"
                    >
                        <X size={14} className="mr-1.5" /> Cancel
                    </Button>
                ) : (
                    <Button
                        variant="outline"
                        onClick={() => setStep((s) => Math.max(1, s - 1))}
                        data-testid={PREFS.backButton}
                        className="rounded-full h-11 px-5 border-[var(--ns-border)]"
                    >
                        <ArrowLeft size={14} className="mr-1.5" /> Back
                    </Button>
                )}
                {step < STEPS.length ? (
                    <Button
                        onClick={() => setStep((s) => Math.min(STEPS.length, s + 1))}
                        data-testid={PREFS.nextButton}
                        className="rounded-full h-11 px-6 text-white font-medium"
                        style={{ background: 'var(--ns-navy)' }}
                    >
                        Next <ArrowRight size={14} className="ml-1.5" />
                    </Button>
                ) : (
                    <Button
                        onClick={() => onSubmit(data)}
                        disabled={submitting}
                        data-testid={PREFS.submitButton}
                        className="rounded-full h-11 px-6 text-white font-medium"
                        style={{ background: 'var(--ns-clay)' }}
                    >
                        {submitting ? 'Saving…' : <><Sparkles size={14} className="mr-1.5" /> Save preferences</>}
                    </Button>
                )}
            </div>
        </div>
    );
}
